import * as React from 'react';
import { useEffect, useState } from 'react';
import { useParams } from 'react-router-dom';
import UserPayroll from '../index';
import UserProfile from '../..';
import {
  GetUserGrossPaysPerMonth,
} from "../../../../api/Payroll/grossPay";
import {
  getUserVariablePaysPerMonth,
} from "../../../../api/Payroll/variablePay";
import { GetUserAdjustmentsPerMonth } from "../../../../api/Payroll/adjustment";
import {
  UserAdjustmentView,
  UserGrossPayView,
  UserVariablePayView,
  UserAdjustmentStatus,
  UserVariablePayStatus,
} from "../../../../models/payroll";
import {
  CompanyCurrencyRate,
  Currency,
} from "../../../../models/general";
import { connect } from "react-redux";
import {
  getCompanyRules,
} from "../../../../api/General/Company";
import { GetCompanyRates } from "../../../../api/General/currency";
import { useTranslation } from 'react-i18next';
import { formatNumber } from '../../../../utils';
import CurrencyDropdown from '../../../../components/currencyDropdown';
import { Table, TableHeader, TableBody, TableRow, TableHead, TableCell } from '../../../../components/ui/Table';
import { Card, CardHeader, CardContent } from '../../../../components/ui/Card';
import { Skeleton } from '../../../../components/ui/Skeleton';

type Props = Readonly<{
  component?: React.ComponentType<any> | undefined;
  role?: any;
  company?: any;
  userId?: any;
  CompanyCurrencyRate?: any;
}>;

const ActivePayroll = (props: Props) => {
  const { t } = useTranslation(['payroll', 'common']);
  const { id } = useParams();
  const [adjustments, setAdjustments] = useState<UserAdjustmentView[]>();
  const [variablePay, setVariablePay] = useState<UserVariablePayView[]>();
  const [grossSalary, setGrossSalary] = useState<UserGrossPayView[]>();
  const [year, setYear] = useState(new Date().getFullYear());
  const [month, setMonth] = useState(new Date().getMonth() + 1);
  const [CompanyCurrencyRateRates, setCompanyCurrencyRateRates] = useState<CompanyCurrencyRate[]>([]);
  const [companyBaseCurrency, setCompanyBaseCurrency] = useState<Currency | null>();
  const [currency, setCurrency] = useState<any>(0);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    fetchData(props.CompanyCurrencyRate?.id);
  }, []);

  const fetchData = async (currencyId: any) => {
    setLoading(true);
    try {
      const userId = id ?? "";

      const fetchedAdjustments = await GetUserAdjustmentsPerMonth(
        userId,
        `${year}-${month.toString().padStart(2, "0")}-01`,
        currencyId
      );
      const adjustments: UserAdjustmentView[] = fetchedAdjustments.payload || [];
      const filteredAdjustments = adjustments.filter((adjustment) => {
        return [UserAdjustmentStatus.Pending, UserAdjustmentStatus.Rejected].indexOf(adjustment.status) == -1;
      });

      setAdjustments(filteredAdjustments);

      const fetchedVariablePay = await getUserVariablePaysPerMonth(
        userId,
        `${year}-${month.toString().padStart(2, "0")}-01`,
        currencyId
      );
      const variablePay: UserVariablePayView[] = fetchedVariablePay.payload || [];
      const filteredVariablePays = variablePay.filter((pay) => {
        return [UserVariablePayStatus.Pending, UserVariablePayStatus.Rejected].indexOf(pay.status) == -1;
      });
      setVariablePay(filteredVariablePays);

      const fetchedGrossSalary = await GetUserGrossPaysPerMonth(
        userId,
        `${year}-${month.toString().padStart(2, "0")}-01`,
        currencyId
      );
      const grossSalary_: UserGrossPayView[] = fetchedGrossSalary.payload || [];
      setGrossSalary(grossSalary_);

      const CompanyCurrencyRateRates = await GetCompanyRates()
      const rates: CompanyCurrencyRate[] = []

      const rules = await getCompanyRules(props.company?.id)
      if (rules.errors?.length > 0 || !rules.payload?.currency) return;
      const baseCurr: CompanyCurrencyRate = {
        id: 0,
        baseCurrency: rules.payload.currency,
        baseCurrencyId: rules.payload.currency.id,
        foreignCurrency: rules.payload.currency,
        foreignCurrencyId: 0,
        company: props.company,
        companyId: props.company?.id,
        rate: 0,
        createdOn: new Date(),
        isArchived: false
      }
      rates.push(baseCurr)
      if (CompanyCurrencyRateRates.errors?.length === 0) {
        const rates_: CompanyCurrencyRate[] = CompanyCurrencyRateRates.payload
        rates.push(...rates_)
      }
      setCompanyBaseCurrency(rules.payload.currency);
      setCompanyCurrencyRateRates(rates);
    } catch {
      // Error handled by interceptor — page shows empty state
    } finally {
      setLoading(false);
    }
  };

  const monthKey = new Date().toLocaleString('en-US', { month: 'long' });
  const translatedMonth = t(`common:${monthKey}`, monthKey);


  return (
    <div className="flex flex-col flex-1">
      <UserProfile>
            <UserPayroll>
              {/* Header */}
              <div className="flex items-center justify-between mb-4">
                <div className="flex items-center gap-3">
                  <h3 className="text-lg font-semibold text-primary">
                    {translatedMonth} {new Date().getFullYear()}
                  </h3>
                </div>
                <div>
                  <CurrencyDropdown
                    value={currency}
                    options={CompanyCurrencyRateRates}
                    onOptionChange={(option: any) => {
                      fetchData(option.foreignCurrencyId);
                      setCurrency(option);
                    }}
                  />
                </div>
              </div>

              {/* Gross Pays */}
              <SectionCard title={t('payroll:Gross Pays')}>
                <StandardTable
                  ariaLabel={t('payroll:Gross Pays') as string}
                  headers={[t('payroll:Pay Items'), t('payroll:Amount'), t('payroll:Remarks')]}
                  rows={grossSalary?.map(g => [g.companyGrossPay?.name, formatNumber(g.amount), g.remarks || '']) || []}
                  loading={loading}
                />
              </SectionCard>

              {/* Variable Pays */}
              <SectionCard title={t('payroll:Variable Pays')}>
                <StandardTable
                  ariaLabel={t('payroll:Variable Pays') as string}
                  headers={[t('payroll:Type'), t('payroll:Amount'), t('payroll:Remarks')]}
                  rows={variablePay?.map(v => [v.companyVariablePayType.name, formatNumber(v.amount), v.remarks || '']) || []}
                  loading={loading}
                />
              </SectionCard>

              {/* Adjustments */}
              <SectionCard title={t('payroll:Adjustments')}>
                <StandardTable
                  ariaLabel={t('payroll:Adjustments') as string}
                  headers={[t('payroll:Type'), t('payroll:Amount'), t('payroll:Remarks')]}
                  rows={adjustments?.map(a => [a.userAdjustmentType, formatNumber(a.amount), a.remarks || '']) || []}
                  loading={loading}
                />
              </SectionCard>
            </UserPayroll>
      </UserProfile>
    </div>
  );
};
const mapStateToProps = (state: any) => {
  return {
    role: state.user.role,
    company: state.user.company,
    CompanyCurrencyRate: state.user.CompanyCurrencyRate,
  };
};
export default connect(mapStateToProps)(ActivePayroll) as any;

/* --- Local presentational helpers --- */
type SectionCardProps = { title: string; children: React.ReactNode };
const SectionCard: React.FC<SectionCardProps> = ({ title, children }) => (
  <section className="flex flex-col gap-3 pt-1">
    <h4 className="text-base font-semibold text-primary">
      {title}
    </h4>
    {children}
  </section>
);

type StandardTableProps = {
  headers: string[];
  rows: (string | number | React.ReactNode)[][];
  ariaLabel: string;
  loading?: boolean;
};
const StandardTable: React.FC<StandardTableProps> = ({ headers, rows, ariaLabel, loading }) => {
  const hasRows = rows && rows.length > 0;
  return (
    <div className="bg-card border border-border rounded-xl overflow-hidden">
      <div className="overflow-x-auto">
        <Table aria-label={ariaLabel}>
          <TableHeader>
            <TableRow>
              {headers.map(h => (
                <TableHead key={h}>{h}</TableHead>
              ))}
            </TableRow>
          </TableHeader>
          <TableBody aria-busy={loading || undefined}>
            {loading && !hasRows ? (
              Array.from({ length: 3 }).map((_, idx) => (
                <TableRow key={`sk-${idx}`}>
                  {headers.map((_h, cidx) => (
                    <TableCell key={cidx}>
                      <Skeleton className={`h-4 ${cidx === 1 ? 'w-20' : 'w-32'} rounded`} />
                    </TableCell>
                  ))}
                </TableRow>
              ))
            ) : hasRows ? (
              rows.map((r, idx) => (
                <TableRow key={idx}>
                  {r.map((c, cidx) => (
                    <TableCell key={cidx} className={cidx === 1 ? 'font-mono' : undefined}>{c}</TableCell>
                  ))}
                </TableRow>
              ))
            ) : (
              <TableRow>
                <TableCell colSpan={headers.length} className="text-muted-foreground text-center">
                  {ariaLabel} – no data
                </TableCell>
              </TableRow>
            )}
          </TableBody>
        </Table>
      </div>
    </div>
  );
};
